import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { pipe } from 'fp-ts/lib/function';
import { FC, useEffect, useState } from 'react';

import { fetchAuctions, fetchPopularAuctions } from '@/shared/api/buyer';
import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';
import { nullableData } from '@/shared/utils/http';

import { ActionVariant, BaseAction } from '../Action';
import { mapRemoteAuctionToCard } from '../Auctions/auctionCard';
import { ComponentHeader } from '../ComponentHeader/ComponentHeader';
import sharedStyle from '../shared.module.scss';
import { Auction, Row } from '../Auctions/Row';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  title?: string;
  description?: string;
  action: BaseAction;
  actionVariant?: ActionVariant;
  categoryId: number;
  categoryLevel: number;
}

type Props = BuilderInput & {
  locale?: string;
  previewAuctions?: Auction[];
};

export const PopularAuctions: FC<Props> = ({
  componentLinkId,
  title,
  description,
  action,
  actionVariant = ActionVariant.NONE,
  categoryId,
  categoryLevel,
  backgroundColorClassName,
  builderBlock,
  builderState,
  locale,
  attributes,
  previewAuctions,
}) => {
  const [auctions, setAuctions] = useState<Auction[]>([]);

  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale || 'en';

  useEffect(() => {
    if (previewAuctions !== undefined) {
      setAuctions(previewAuctions);
      return;
    }

    const loadAuctions = async () => {
      try {
        const eitherAuctionIds = await fetchPopularAuctions(
          blockLocale,
          categoryId,
          categoryLevel
        );
        const auctionIds = pipe(eitherAuctionIds, nullableData) || [];

        if (auctionIds.length === 0) {
          setAuctions([]);
          return;
        }

        const eitherAuctions = await fetchAuctions(blockLocale, auctionIds);
        const remoteAuctions = pipe(eitherAuctions, nullableData) || [];

        const auctionsMap = remoteAuctions.reduce(
          (out, auction) => ({
            ...out,
            [auction.id]: mapRemoteAuctionToCard(auction),
          }),
          {} as {
            [key in number]: Auction;
          }
        );

        // Preserve the order of auctionIds
        setAuctions(
          auctionIds.map((id: number) => auctionsMap[id]).filter(Boolean)
        );
      } catch {
        setAuctions([]);
      }
    };

    loadAuctions();
  }, [blockLocale, categoryId, categoryLevel, previewAuctions]);

  return (
    <div
      {...attributes}
      id={componentLinkId}
      data-testid="popular-auctions"
      className={cn(
        sharedStyle.section,
        builderStyles,
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <ComponentHeader
        title={title}
        description={description}
        action={action}
        actionVariant={actionVariant}
        locale={blockLocale}
        headerClassName={cn('u-wrapper', {
          'u-m-b-xl u-m-b-lg-xxl-6': auctions && auctions.length > 0,
        })}
        mobileActionClassName={'u-wrapper'}
      >
        <Row locale={blockLocale} auctions={auctions} />
      </ComponentHeader>
    </div>
  );
};

export const NAME = 'PopularAuctions';

export function componentRegister() {
  Builder.registerComponent(PopularAuctions, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'html',
        defaultValue:
          'Section <span class="u-typography-h2-serif tw:text-brand-primary">Title</span>',
      },
      {
        name: 'description',
        type: 'html',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
        useCatawikiPlainEditor: false,
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'categoryId',
        friendlyName: 'Category ID',
        type: 'number',
        required: true,
      },
      {
        name: 'categoryLevel',
        friendlyName: 'Category Level',
        type: 'number',
        required: true,
        defaultValue: 0,
      },
    ],
    noWrap: true,
  });

  return NAME;
}
